import { useState, useRef, useEffect, useCallback } from "react";
import {
  Pencil, ArrowUpRight, Square, Circle, Highlighter, Type,
  Undo2, Eraser, Check, ChevronLeft, Hand,
} from "lucide-react";
import { TINTA, MARCA, ACCION, COLORES } from "../constants.js";
import { uid } from "../lib/util.js";
import { normalizarFormas } from "../lib/formas.js";

// Recorrido mínimo (en px de pantalla) para confirmar una forma. Por debajo se
// considera un toque suelto y se descarta, así desplazar no deja flechas (S-014).
const UMBRAL_TRAZO_PX = 10;

/* ============================================================
   EDITOR DE ANOTACIONES
   Dibuja la imagen base en un canvas y superpone las formas.
   ============================================================ */
export default function Editor({
  imagenBase,
  capturaExistente,
  urlOrigen,
  onGuardar,
  onCancelar,
}) {
  const [herr, setHerr] = useState("arrow");
  const [color, setColor] = useState(MARCA);
  const [formas, setFormas] = useState(capturaExistente?.formas || []);
  const [titulo, setTitulo] = useState(capturaExistente?.titulo || "");
  const [nota, setNota] = useState(capturaExistente?.nota || "");
  const [textoPend, setTextoPend] = useState(null); // {x,y}
  const [textoVal, setTextoVal] = useState("");
  const [modoPan, setModoPan] = useState(false);
  const canvasRef = useRef(null);
  const scrollRef = useRef(null); // contenedor con overflow que se desplaza en modo pan
  const imgRef = useRef(null);
  const formaTemp = useRef(null);
  const trazoRef = useRef(null); // {inicioX, inicioY, supero, pointerId} en px de cliente
  const punteros = useRef(new Map()); // pointerId -> {x, y} (px de cliente) de los activos
  const modoPanRef = useRef(false); // espejo síncrono de modoPan para los handlers
  const panRef = useRef({ activo: false, x: 0, y: 0 }); // centroide previo del pan
  const [imgLista, setImgLista] = useState(false);

  // Pista táctil solo donde tiene sentido (tablet/móvil), no con ratón.
  const esTactil =
    typeof navigator !== "undefined" &&
    (navigator.maxTouchPoints > 0 || "ontouchstart" in window);

  const fuente = capturaExistente?.imagenOriginal || imagenBase;

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      setImgLista(true);
    };
    img.src = fuente;
  }, [fuente]);

  const redibujar = useCallback(
    (extra) => {
      const cv = canvasRef.current,
        img = imgRef.current;
      if (!cv || !img) return;
      cv.width = img.width;
      cv.height = img.height;
      const ctx = cv.getContext("2d");
      ctx.drawImage(img, 0, 0);
      // Se sanea el arreglo: una forma inválida (null) NO debe llegar al dibujado
      // ni tumbar la app (ver S-013). `extra` es la forma en curso (siempre válida).
      const todas = normalizarFormas(extra ? [...formas, extra] : formas);
      const grosor = Math.max(3, cv.width / 320);
      todas.forEach((f) => dibujarForma(ctx, f, grosor, cv.width));
    },
    [formas]
  );

  useEffect(() => {
    if (imgLista) redibujar();
  }, [imgLista, redibujar]);

  function dibujarForma(ctx, f, grosor, anchoCv) {
    if (!f || typeof f.color !== "string") return; // guarda defensiva (S-013)
    ctx.save();
    ctx.strokeStyle = f.color;
    ctx.fillStyle = f.color;
    ctx.lineWidth = grosor;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (f.t === "pen") {
      ctx.beginPath();
      f.pts.forEach((p, i) =>
        i === 0 ? ctx.moveTo(p[0], p[1]) : ctx.lineTo(p[0], p[1])
      );
      ctx.stroke();
    } else if (f.t === "arrow") {
      const [x1, y1, x2, y2] = [f.x1, f.y1, f.x2, f.y2];
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      const ang = Math.atan2(y2 - y1, x2 - x1),
        L = grosor * 4;
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - L * Math.cos(ang - 0.45), y2 - L * Math.sin(ang - 0.45));
      ctx.lineTo(x2 - L * Math.cos(ang + 0.45), y2 - L * Math.sin(ang + 0.45));
      ctx.closePath();
      ctx.fill();
    } else if (f.t === "rect") {
      ctx.strokeRect(f.x1, f.y1, f.x2 - f.x1, f.y2 - f.y1);
    } else if (f.t === "ellipse") {
      ctx.beginPath();
      ctx.ellipse(
        (f.x1 + f.x2) / 2,
        (f.y1 + f.y2) / 2,
        Math.abs(f.x2 - f.x1) / 2,
        Math.abs(f.y2 - f.y1) / 2,
        0,
        0,
        Math.PI * 2
      );
      ctx.stroke();
    } else if (f.t === "highlight") {
      ctx.globalAlpha = 0.28;
      ctx.fillStyle = "#F2C511";
      ctx.fillRect(f.x1, f.y1, f.x2 - f.x1, f.y2 - f.y1);
    } else if (f.t === "text") {
      const fs = Math.max(18, anchoCv / 38);
      ctx.font = `bold ${fs}px sans-serif`;
      const m = ctx.measureText(f.txt);
      ctx.globalAlpha = 0.92;
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(f.x - fs * 0.3, f.y - fs * 1.1, m.width + fs * 0.6, fs * 1.5);
      ctx.globalAlpha = 1;
      ctx.fillStyle = f.color;
      ctx.fillText(f.txt, f.x, f.y);
      ctx.strokeStyle = f.color;
      ctx.lineWidth = Math.max(1.5, grosor / 2);
      ctx.strokeRect(f.x - fs * 0.3, f.y - fs * 1.1, m.width + fs * 0.6, fs * 1.5);
    }
    ctx.restore();
  }

  function coords(e) {
    const cv = canvasRef.current;
    const r = cv.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * cv.width;
    const y = ((e.clientY - r.top) / r.height) * cv.height;
    return [x, y];
  }

  /* --- Gestos del lienzo (ver S-014) -------------------------------------
     touch-action:none entrega TODO gesto como punteros. Reglas:
      · Un puntero (dedo/ratón/lápiz) dibuja; la forma solo se confirma si el
        recorrido supera UMBRAL_TRAZO_PX (así desplazar no deja flechas sueltas).
      · Un SEGUNDO dedo cancela el trazo en curso y pasa a desplazar la vista
        (convención Figma/Excalidraw). Se vuelve al modo normal con 0 punteros.
     El texto es un toque directo y NO está sujeto al umbral. */

  function cancelarTrazoEnCurso() {
    formaTemp.current = null;
    trazoRef.current = null;
    redibujar(); // limpia el trazo temporal que se estuviera mostrando
  }

  function entrarModoPan() {
    modoPanRef.current = true;
    setModoPan(true);
    panRef.current = { activo: false, x: 0, y: 0 };
  }

  function salirModoPan() {
    modoPanRef.current = false;
    setModoPan(false);
    panRef.current = { activo: false, x: 0, y: 0 };
  }

  function hacerPan() {
    const pts = [...punteros.current.values()];
    if (pts.length < 2) return; // se necesita el centroide de 2+ dedos
    const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
    const cont = scrollRef.current;
    if (panRef.current.activo && cont) {
      cont.scrollLeft -= cx - panRef.current.x;
      cont.scrollTop -= cy - panRef.current.y;
    }
    panRef.current = { activo: true, x: cx, y: cy };
  }

  function alPresionar(e) {
    e.preventDefault();
    canvasRef.current?.setPointerCapture?.(e.pointerId);
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Segundo puntero → cancelar el trazo y desplazar (multitáctil).
    if (punteros.current.size >= 2) {
      cancelarTrazoEnCurso();
      entrarModoPan();
      return;
    }
    if (modoPanRef.current) return;

    // Texto: toque directo, sin umbral.
    if (herr === "text") {
      const [tx, ty] = coords(e);
      setTextoPend({ x: tx, y: ty });
      setTextoVal("");
      return;
    }

    // Iniciar un trazo de un solo puntero.
    const [x, y] = coords(e);
    trazoRef.current = {
      inicioX: e.clientX,
      inicioY: e.clientY,
      supero: false,
      pointerId: e.pointerId,
    };
    if (herr === "pen") formaTemp.current = { t: "pen", color, pts: [[x, y]] };
    else formaTemp.current = { t: herr, color, x1: x, y1: y, x2: x, y2: y };
  }

  function alMover(e) {
    const p = punteros.current.get(e.pointerId);
    if (p) {
      p.x = e.clientX;
      p.y = e.clientY;
    }

    if (modoPanRef.current) {
      hacerPan();
      return;
    }

    if (
      !formaTemp.current ||
      !trazoRef.current ||
      e.pointerId !== trazoRef.current.pointerId
    )
      return;

    // ¿Ya se movió lo suficiente para considerarlo un trazo intencional?
    if (!trazoRef.current.supero) {
      const dx = e.clientX - trazoRef.current.inicioX;
      const dy = e.clientY - trazoRef.current.inicioY;
      if (Math.hypot(dx, dy) >= UMBRAL_TRAZO_PX) trazoRef.current.supero = true;
    }

    const [x, y] = coords(e);
    if (formaTemp.current.t === "pen") formaTemp.current.pts.push([x, y]);
    else {
      formaTemp.current.x2 = x;
      formaTemp.current.y2 = y;
    }
    redibujar(formaTemp.current);
  }

  function alSoltar(e) {
    punteros.current.delete(e.pointerId);

    if (modoPanRef.current) {
      if (punteros.current.size === 0) salirModoPan();
      else panRef.current.activo = false; // se levantó un dedo: rebasa el baseline
      return;
    }

    if (
      !formaTemp.current ||
      !trazoRef.current ||
      e.pointerId !== trazoRef.current.pointerId
    ) {
      formaTemp.current = null;
      trazoRef.current = null;
      return;
    }

    // CAUSA RAÍZ S-013: el updater de setFormas es diferido; se captura la forma
    // en una local ANTES de limpiar el ref para no encolar un `null`.
    const supero = trazoRef.current.supero;
    const nueva = formaTemp.current;
    formaTemp.current = null;
    trazoRef.current = null;

    if (!supero) {
      redibujar(); // recorrido bajo umbral (toque suelto): se descarta (S-014)
      return;
    }
    setFormas((f) => [...f, nueva]);
  }

  function confirmarTexto() {
    if (textoVal.trim()) {
      setFormas((f) => [
        ...f,
        { t: "text", color, x: textoPend.x, y: textoPend.y, txt: textoVal.trim() },
      ]);
    }
    setTextoPend(null);
    setTextoVal("");
  }

  function guardar() {
    const cv = canvasRef.current;
    redibujar();
    const final = cv.toDataURL("image/jpeg", 0.82);
    onGuardar({
      id: capturaExistente?.id || uid(),
      titulo: titulo.trim() || "Sin título",
      nota: nota.trim(),
      url: capturaExistente?.url || urlOrigen || "",
      fecha: capturaExistente?.fecha || new Date().toISOString(),
      imagen: final,
      imagenOriginal: fuente,
      formas: normalizarFormas(formas), // nunca persistir formas inválidas (S-013)
    });
  }

  const HERRAMIENTAS = [
    { id: "arrow", icono: ArrowUpRight, nombre: "Flecha" },
    { id: "rect", icono: Square, nombre: "Rectángulo" },
    { id: "ellipse", icono: Circle, nombre: "Círculo" },
    { id: "pen", icono: Pencil, nombre: "Lápiz" },
    { id: "highlight", icono: Highlighter, nombre: "Resaltar" },
    { id: "text", icono: Type, nombre: "Texto" },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* barra de herramientas */}
      <div
        className="flex flex-wrap items-center gap-2 p-3 bg-white border-b"
        style={{ borderColor: "#E2E5EB" }}
      >
        <button
          onClick={onCancelar}
          className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-100"
          style={{ color: TINTA }}
        >
          <ChevronLeft size={16} /> Volver
        </button>
        <div className="w-px h-6 bg-gray-200" />
        {HERRAMIENTAS.map((h) => (
          <button
            key={h.id}
            onClick={() => setHerr(h.id)}
            title={h.nombre}
            className="p-2.5 rounded-lg transition-colors"
            style={
              herr === h.id ? { background: TINTA, color: "#fff" } : { color: TINTA }
            }
          >
            <h.icono size={18} />
          </button>
        ))}
        <div className="w-px h-6 bg-gray-200" />
        {COLORES.map((c) => (
          <button
            key={c}
            onClick={() => setColor(c)}
            className="w-7 h-7 rounded-full border-2 transition-transform"
            style={{
              background: c,
              borderColor: color === c ? TINTA : "transparent",
              transform: color === c ? "scale(1.15)" : "scale(1)",
            }}
          />
        ))}
        <div className="w-px h-6 bg-gray-200" />
        <button
          onClick={() => setFormas((f) => f.slice(0, -1))}
          title="Deshacer"
          className="p-2.5 rounded-lg hover:bg-gray-100"
          style={{ color: TINTA }}
        >
          <Undo2 size={18} />
        </button>
        <button
          onClick={() => setFormas([])}
          title="Borrar todo"
          className="p-2.5 rounded-lg hover:bg-gray-100"
          style={{ color: TINTA }}
        >
          <Eraser size={18} />
        </button>
        <div className="flex-1" />
        <button
          onClick={guardar}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
          style={{ background: ACCION }}
        >
          <Check size={16} /> Guardar captura
        </button>
      </div>

      {/* lienzo */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-auto p-4 flex justify-center items-start"
        style={{ background: "#E8EAEF" }}
      >
        <div className="relative max-w-4xl w-full shadow-lg rounded-lg overflow-hidden bg-white">
          <canvas
            ref={canvasRef}
            className="w-full block"
            style={{
              touchAction: "none",
              cursor: modoPan ? "grabbing" : "crosshair",
            }}
            onPointerDown={alPresionar}
            onPointerMove={alMover}
            onPointerUp={alSoltar}
            onPointerCancel={alSoltar}
          />
          {/* Indicador de gesto: badge activo en modo pan; pista sutil en táctil. */}
          {modoPan ? (
            <div
              className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white shadow"
              style={{ background: MARCA }}
            >
              <Hand size={14} /> Desplazando la vista
            </div>
          ) : (
            esTactil && (
              <div
                className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] bg-white/90 border shadow-sm"
                style={{ borderColor: "#E2E5EB", color: "#5B6472" }}
              >
                <Hand size={12} /> Un dedo dibuja · dos dedos desplazan
              </div>
            )
          )}
          {textoPend && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl p-4 w-full max-w-sm shadow-xl">
                <p className="text-sm font-semibold mb-2" style={{ color: TINTA }}>
                  Texto de la anotación
                </p>
                <input
                  autoFocus
                  value={textoVal}
                  onChange={(e) => setTextoVal(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && confirmarTexto()}
                  className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
                  style={{ borderColor: "#CBD2DC" }}
                  placeholder="Ej.: Cambiar este botón"
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setTextoPend(null)}
                    className="px-3 py-1.5 text-sm rounded-lg hover:bg-gray-100"
                    style={{ color: TINTA }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={confirmarTexto}
                    className="px-3 py-1.5 text-sm rounded-lg text-white font-medium"
                    style={{ background: ACCION }}
                  >
                    Colocar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* metadatos */}
      <div
        className="p-3 bg-white border-t flex flex-col sm:flex-row gap-2"
        style={{ borderColor: "#E2E5EB" }}
      >
        <input
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Título de la observación"
          className="flex-1 border rounded-lg px-3 py-2 text-sm"
          style={{ borderColor: "#CBD2DC" }}
        />
        <input
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          placeholder="Indicación o mejora sugerida"
          className="flex-[2] border rounded-lg px-3 py-2 text-sm"
          style={{ borderColor: "#CBD2DC" }}
        />
      </div>
    </div>
  );
}
